<template>
  <article class="sd-page cc">
    <p v-if="datos.demo || !publicada" class="cc-demo" role="status">
      <span class="material-symbols-outlined">science</span>
      <span class="cc-demo-texto">
        <strong>Página de demostración.</strong>
        <template v-if="datos.demo"> Los datos de Bizum, cuenta y el formulario son de ejemplo.</template>
        <template v-if="!publicada"> No aparece en el menú ni en Google hasta que se publique.</template>
        Se cambian en <code>src/contenido/config.js</code>.
      </span>
    </p>

    <!-- Cabecera -->
    <header class="cc-hero">
      <div class="cc-hero-text">
        <p class="sd-eyebrow">Cáritas parroquial</p>
        <h1 class="sd-title cc-title">Colabora con Cáritas</h1>
        <p class="cc-lead">
          Cada semana, Cáritas de Santo Domingo acompaña a familias de nuestro barrio que lo están pasando mal.
          Tu ayuda, por pequeña que sea, se convierte en alimentos, en recibos pagados y en tiempo compartido.
        </p>
        <div class="cc-hero-actions">
          <a href="#donar" class="sd-btn" @click.prevent="irA('donar')">
            <span class="material-symbols-outlined">volunteer_activism</span> Hacer un donativo
          </a>
          <a href="#voluntariado" class="sd-btn sd-btn--ghost" @click.prevent="irA('voluntariado')">
            <span class="material-symbols-outlined">diversity_3</span> Hazte voluntario
          </a>
        </div>
      </div>
      <div class="cc-hero-media" aria-hidden="true">
        <span class="material-symbols-outlined">handshake</span>
      </div>
    </header>

    <!-- A qué se destina -->
    <section class="cc-section" aria-labelledby="cc-impacto">
      <h2 id="cc-impacto" class="cc-h2">Qué hacemos con tu ayuda</h2>
      <p class="cc-section-sub">Ejemplos orientativos de lo que permite cada aportación.</p>
      <ul class="cc-impacto">
        <li v-for="i in impacto" :key="i.importe" class="cc-impacto-item">
          <span class="cc-impacto-importe">{{ i.importe }}</span>
          <span class="material-symbols-outlined cc-impacto-icon" aria-hidden="true">{{ i.icono }}</span>
          <span class="cc-impacto-texto">{{ i.texto }}</span>
        </li>
      </ul>
    </section>

    <!-- Formas de donar -->
    <section id="donar" class="cc-section" aria-labelledby="cc-donar">
      <h2 id="cc-donar" class="cc-h2">Haz un donativo</h2>
      <p class="cc-section-sub">Elige la forma que te resulte más cómoda. El 100&nbsp;% se destina a la acción social de la parroquia.</p>

      <div class="cc-formas">
        <!-- Bizum -->
        <article class="cc-forma cc-forma--bizum">
          <header class="cc-forma-head">
            <span class="cc-forma-logo" aria-hidden="true">Bizum</span>
            <h3 class="cc-h3">Bizum</h3>
          </header>
          <p class="cc-forma-texto">Desde la app de tu banco: <strong>Bizum → Donar a una ONG</strong> e introduce el código:</p>
          <div class="cc-copiable">
            <span class="cc-codigo">{{ datos.bizum }}</span>
            <button type="button" class="cc-copiar" @click="copiar(datos.bizum, 'bizum')">
              <span class="material-symbols-outlined">{{ copiado === "bizum" ? "check" : "content_copy" }}</span>
              {{ copiado === "bizum" ? "Copiado" : "Copiar" }}
            </button>
          </div>
          <p class="cc-nota">Es inmediato y sin comisiones.</p>
        </article>

        <!-- Transferencia -->
        <article class="cc-forma">
          <header class="cc-forma-head">
            <span class="cc-forma-icon material-symbols-outlined" aria-hidden="true">account_balance</span>
            <h3 class="cc-h3">Transferencia</h3>
          </header>
          <dl class="cc-cuenta">
            <dt>IBAN</dt>
            <dd class="cc-copiable">
              <span class="cc-iban">{{ datos.iban }}</span>
              <button type="button" class="cc-copiar" @click="copiar(datos.iban.replace(/\s/g, ''), 'iban')">
                <span class="material-symbols-outlined">{{ copiado === "iban" ? "check" : "content_copy" }}</span>
                {{ copiado === "iban" ? "Copiado" : "Copiar" }}
              </button>
            </dd>
            <dt>Titular</dt>
            <dd>{{ datos.titular }}</dd>
            <dt>Concepto</dt>
            <dd>{{ datos.concepto }}</dd>
          </dl>
          <p class="cc-nota">Puedes programarla como mensual: una ayuda fija nos permite planificar mejor.</p>
        </article>

        <!-- En la parroquia -->
        <article class="cc-forma">
          <header class="cc-forma-head">
            <span class="cc-forma-icon material-symbols-outlined" aria-hidden="true">church</span>
            <h3 class="cc-h3">En la parroquia</h3>
          </header>
          <p class="cc-forma-texto">
            En el cepillo de Cáritas de la iglesia o en el despacho parroquial. También recogemos
            <strong>alimentos no perecederos</strong> y productos de higiene.
          </p>
          <a :href="mapsUrl" target="_blank" rel="noopener" class="cc-link" v-track:contacto="{ metodo: 'mapa', origen: 'caritas_colabora' }">
            <span class="material-symbols-outlined">location_on</span> {{ direccion }}
          </a>
        </article>
      </div>

      <p class="cc-certificado">
        <span class="material-symbols-outlined" aria-hidden="true">receipt_long</span>
        <span class="cc-certificado-texto">
          ¿Necesitas un certificado de donación para la declaración de la renta? Escríbenos a
          <a :href="`mailto:${emailCaritas}`">{{ emailCaritas }}</a> con tu nombre, DNI y el importe.
        </span>
      </p>
    </section>

    <!-- Voluntariado -->
    <section id="voluntariado" class="cc-section cc-voluntariado" aria-labelledby="cc-vol">
      <div class="cc-vol-intro">
        <h2 id="cc-vol" class="cc-h2">Hazte voluntario</h2>
        <p class="cc-section-sub">
          No hace falta experiencia: solo ganas de ayudar y un poco de tiempo. Cuéntanos qué te gustaría hacer
          y te llamaremos para conocernos.
        </p>
        <ul class="cc-areas-lista">
          <li v-for="a in areas" :key="a.valor">
            <span class="material-symbols-outlined" aria-hidden="true">{{ a.icono }}</span>
            <span class="cc-areas-texto">{{ a.texto }}</span>
          </li>
        </ul>
      </div>

      <form class="cc-form" novalidate @submit.prevent="enviar">
        <div v-if="enviado" class="cc-form-ok" role="status">
          <span class="material-symbols-outlined">task_alt</span>
          <h3 class="cc-h3">¡Gracias, {{ form.nombre.split(" ")[0] }}!</h3>
          <p v-if="datos.formularioActivo">Se ha abierto tu correo con la solicitud. Envíalo y nos pondremos en contacto contigo.</p>
          <p v-else>
            <strong>(Demostración)</strong> Así se confirmaría la solicitud. No se ha enviado ningún dato.
          </p>
          <button type="button" class="cc-link" @click="reiniciar">Enviar otra solicitud</button>
        </div>

        <template v-else>
          <div class="cc-campo">
            <label for="cc-nombre">Nombre y apellidos</label>
            <input id="cc-nombre" v-model.trim="form.nombre" type="text" autocomplete="name" required maxlength="80" />
          </div>
          <div class="cc-fila">
            <div class="cc-campo">
              <label for="cc-telefono">Teléfono</label>
              <input id="cc-telefono" v-model.trim="form.telefono" type="tel" autocomplete="tel" inputmode="tel" maxlength="20" />
            </div>
            <div class="cc-campo">
              <label for="cc-email">Correo electrónico</label>
              <input id="cc-email" v-model.trim="form.email" type="email" autocomplete="email" maxlength="120" />
            </div>
          </div>

          <fieldset class="cc-campo">
            <legend>¿En qué te gustaría ayudar?</legend>
            <div class="cc-chips">
              <label v-for="a in areas" :key="a.valor" class="cc-chip" :class="{ 'is-on': form.areas.includes(a.valor) }">
                <input v-model="form.areas" type="checkbox" :value="a.valor" />
                {{ a.corto }}
              </label>
            </div>
          </fieldset>

          <fieldset class="cc-campo">
            <legend>Disponibilidad</legend>
            <div class="cc-chips">
              <label v-for="d in disponibilidades" :key="d" class="cc-chip" :class="{ 'is-on': form.disponibilidad.includes(d) }">
                <input v-model="form.disponibilidad" type="checkbox" :value="d" />
                {{ d }}
              </label>
            </div>
          </fieldset>

          <div class="cc-campo">
            <label for="cc-mensaje">Mensaje <small>(opcional)</small></label>
            <textarea id="cc-mensaje" v-model.trim="form.mensaje" rows="3" maxlength="600"></textarea>
          </div>

          <label class="cc-check">
            <input v-model="form.privacidad" type="checkbox" required />
            <span class="cc-check-texto">
              Acepto que Cáritas parroquial use estos datos solo para contactarme sobre el voluntariado.
            </span>
          </label>

          <p v-if="error" class="cc-error" role="alert">{{ error }}</p>

          <button type="submit" class="sd-btn cc-enviar">
            <span class="material-symbols-outlined">send</span> Quiero ser voluntario
          </button>
        </template>
      </form>
    </section>

    <footer class="cc-pie">
      <router-link :to="{ name: 'CaritasParroquialNoticias' }" class="cc-link">
        <span class="material-symbols-outlined">newspaper</span> Noticias de Cáritas
      </router-link>
      <botones-compartir
        etiqueta="Ayúdanos a difundirlo"
        compacto
        titulo="Colabora con Cáritas Santo Domingo"
        texto="Dona o hazte voluntario de Cáritas parroquial en Cartagena"
        ruta="/caritas/colabora"
        tipo="caritas"
      />
    </footer>
  </article>
</template>

<script>
import BotonesCompartir from "@/components/compartir/BotonesCompartir.vue";
import { CARITAS_DONATIVOS, PAGINAS } from "@/contenido/config.js";
import { Constantes } from "@/directives/Constantes";
import { track } from "@/directives/analitica.js";

const formVacio = () => ({
  nombre: "",
  telefono: "",
  email: "",
  areas: [],
  disponibilidad: [],
  mensaje: "",
  privacidad: false,
});

export default {
  name: "CaritasColabora",
  components: { BotonesCompartir },
  metaInfo() {
    return {
      title: `Colabora con Cáritas · ${this.$root.constantes.nombre_sitio}`,
      meta: [
        { vmid: "description", name: "description", content: "Haz un donativo o hazte voluntario de Cáritas parroquial de Santo Domingo, en Cartagena." },
        ...(this.publicada ? [] : [{ vmid: "robots", name: "robots", content: "noindex, nofollow" }]),
      ],
      link: [{ rel: "canonical", href: `${this.$root.constantes.sitio_web}/caritas/colabora` }],
    };
  },
  data() {
    return {
      datos: CARITAS_DONATIVOS,
      publicada: PAGINAS.caritasColabora.publicada,
      emailCaritas: Constantes.email.caritas,
      direccion: (Constantes.direccion || "").trim(),
      copiado: "",
      enviado: false,
      error: "",
      form: formVacio(),
      // Importes de ejemplo: ajústalos a la realidad de Cáritas parroquial
      impacto: [
        { importe: "10 €", icono: "shopping_basket", texto: "Productos básicos para una familia durante una semana." },
        { importe: "30 €", icono: "child_care", texto: "Leche, pañales y alimentación infantil para un mes." },
        { importe: "50 €", icono: "bolt", texto: "Ayuda para pagar un recibo de luz o agua." },
        { importe: "100 €", icono: "home", texto: "Apoyo con el alquiler a una familia en riesgo de perder su casa." },
      ],
      areas: [
        { valor: "alimentos", corto: "Reparto de alimentos", texto: "Recogida y reparto de alimentos", icono: "inventory_2" },
        { valor: "acogida", corto: "Acogida", texto: "Acogida y escucha a las familias", icono: "forum" },
        { valor: "mayores", corto: "Mayores", texto: "Acompañamiento a personas mayores", icono: "elderly" },
        { valor: "infancia", corto: "Infancia", texto: "Apoyo escolar a niños y jóvenes", icono: "school" },
        { valor: "gestion", corto: "Gestión", texto: "Administración, difusión y campañas", icono: "campaign" },
      ],
      disponibilidades: ["Mañanas", "Tardes", "Fines de semana", "Puntualmente"],
    };
  },
  computed: {
    mapsUrl() {
      return "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(this.direccion);
    },
  },
  methods: {
    irA(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    async copiar(texto, cual) {
      try {
        await navigator.clipboard.writeText(texto);
      } catch (e) {
        return;
      }
      this.copiado = cual;
      track("donativo_caritas", { metodo: cual, accion: "copiar" });
      setTimeout(() => (this.copiado = ""), 2000);
    },
    enviar() {
      this.error = "";
      const f = this.form;
      if (f.nombre.length < 3) return (this.error = "Escribe tu nombre.");
      if (!f.telefono && !f.email) return (this.error = "Déjanos un teléfono o un correo para poder contactarte.");
      if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) return (this.error = "Revisa el correo electrónico.");
      if (!f.privacidad) return (this.error = "Necesitamos tu permiso para guardar estos datos.");

      if (this.datos.formularioActivo) {
        const areas = this.areas.filter((a) => f.areas.includes(a.valor)).map((a) => a.texto);
        const cuerpo = [
          `Nombre: ${f.nombre}`,
          f.telefono && `Teléfono: ${f.telefono}`,
          f.email && `Correo: ${f.email}`,
          areas.length && `Me gustaría ayudar en: ${areas.join(", ")}`,
          f.disponibilidad.length && `Disponibilidad: ${f.disponibilidad.join(", ")}`,
          f.mensaje && `\n${f.mensaje}`,
        ].filter(Boolean).join("\n");
        window.location.href = `mailto:${this.emailCaritas}?subject=${encodeURIComponent("Quiero ser voluntario de Cáritas")}&body=${encodeURIComponent(cuerpo)}`;
      }

      // Solo se mide que se envió, nunca los datos personales
      track("voluntariado_caritas", { areas: f.areas.join(",") || "sin_indicar", demo: this.datos.formularioActivo ? "no" : "si" });
      this.enviado = true;
    },
    reiniciar() {
      this.form = formVacio();
      this.enviado = false;
    },
  },
};
</script>

<style scoped>
.cc-demo {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 28px;
  padding: 14px 18px;
  border: 1px dashed var(--sd-warning);
  border-radius: 14px;
  background: var(--sd-warning-soft);
  font-size: 0.9rem;
}

.cc-demo .material-symbols-outlined {
  color: var(--sd-warning);
}

.cc-demo-texto {
  color: var(--sd-text);
}

/* Cabecera */
.cc-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  align-items: center;
  gap: 40px;
  margin-bottom: 56px;
}

.cc-title {
  font-size: clamp(2rem, 4.4vw, 3.2rem);
}

.cc-lead {
  margin: 16px 0 0;
  font-size: 1.12rem;
  line-height: 1.65;
  color: var(--sd-muted);
}

.cc-hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 26px;
}

.cc-hero-media {
  display: grid;
  place-items: center;
  aspect-ratio: 1;
  max-width: 360px;
  width: 100%;
  justify-self: center;
  border-radius: 50%;
  background:
    radial-gradient(circle at 35% 30%, rgba(220, 38, 38, 0.16), transparent 60%),
    radial-gradient(circle at 70% 75%, var(--sd-gold-soft), transparent 60%),
    var(--sd-surface-2);
}

.cc-hero-media .material-symbols-outlined {
  font-size: clamp(110px, 14vw, 170px);
  color: #c81e1e;
}

.dark .cc-hero-media .material-symbols-outlined {
  color: #f87171;
}

/* Secciones */
.cc-section {
  margin-bottom: 64px;
  scroll-margin-top: 120px;
}

.cc-h2 {
  margin: 0;
  font-family: var(--sd-font-display);
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  color: var(--sd-text);
}

.cc-h3 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--sd-text);
}

.cc-section-sub {
  margin: 8px 0 24px;
  max-width: 680px;
  color: var(--sd-muted);
}

/* Impacto */
.cc-impacto {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 210px), 1fr));
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.cc-impacto-item {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas: "importe icono" "texto texto";
  align-items: center;
  gap: 10px;
  padding: 20px;
  border: 1px solid var(--sd-border);
  border-radius: 18px;
  background: var(--sd-surface);
}

.cc-impacto-importe {
  grid-area: importe;
  font-family: var(--sd-font-display);
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--sd-primary);
}

.cc-impacto-icon.material-symbols-outlined {
  grid-area: icono;
  justify-self: end;
  font-size: 30px;
  color: var(--sd-gold);
}

.cc-impacto-texto {
  grid-area: texto;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--sd-text);
}

/* Formas de donar */
.cc-formas {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 290px), 1fr));
  gap: 18px;
}

.cc-forma {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  border: 1px solid var(--sd-border);
  border-radius: 20px;
  background: var(--sd-surface);
  box-shadow: var(--sd-shadow);
}

.cc-forma--bizum {
  border-color: rgba(5, 192, 189, 0.5);
}

.cc-forma-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cc-forma-logo {
  padding: 6px 12px;
  border-radius: 10px;
  background: #05c0bd;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #fff;
}

.cc-forma-icon.material-symbols-outlined {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: var(--sd-primary-soft);
  font-size: 24px;
  color: var(--sd-primary);
}

.cc-forma-texto {
  margin: 0;
  line-height: 1.55;
  color: var(--sd-text);
}

.cc-copiable {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 0;
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--sd-surface-2);
}

.cc-codigo {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: var(--sd-text);
}

.cc-iban {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--sd-text);
  overflow-wrap: anywhere;
}

.cc-copiar {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border: 1px solid var(--sd-border);
  border-radius: 999px;
  background: var(--sd-surface);
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--sd-text);
  cursor: pointer;
}

.cc-copiar:hover {
  border-color: var(--sd-primary);
  color: var(--sd-primary);
}

.cc-copiar .material-symbols-outlined {
  font-size: 17px;
  color: inherit;
}

.cc-cuenta {
  display: grid;
  gap: 4px;
  margin: 0;
}

.cc-cuenta dt {
  margin-top: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--sd-muted);
}

.cc-cuenta dd {
  margin: 0;
  color: var(--sd-text);
}

.cc-nota {
  margin: auto 0 0;
  font-size: 0.86rem;
  color: var(--sd-muted);
}

.cc-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: none;
  font-weight: 600;
  color: var(--sd-primary);
  text-decoration: none;
  cursor: pointer;
}

.cc-link:hover {
  text-decoration: underline;
}

.cc-link .material-symbols-outlined {
  font-size: 19px;
  color: inherit;
}

.cc-certificado {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 22px 0 0;
  padding: 14px 18px;
  border-radius: 14px;
  background: var(--sd-primary-soft);
  font-size: 0.92rem;
}

.cc-certificado .material-symbols-outlined {
  color: var(--sd-primary);
}

.cc-certificado-texto {
  color: var(--sd-text);
}

/* Voluntariado */
.cc-voluntariado {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
  align-items: start;
  gap: 40px;
}

.cc-areas-lista {
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.cc-areas-lista li {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cc-areas-lista .material-symbols-outlined {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: var(--sd-gold-soft);
  font-size: 21px;
  color: var(--sd-gold);
}

.cc-areas-texto {
  color: var(--sd-text);
}

.cc-form {
  display: grid;
  gap: 16px;
  padding: clamp(20px, 3vw, 30px);
  border: 1px solid var(--sd-border);
  border-radius: 22px;
  background: var(--sd-surface);
  box-shadow: var(--sd-shadow-lg);
}

.cc-fila {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 16px;
}

.cc-campo {
  display: grid;
  gap: 6px;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.cc-campo label,
.cc-campo legend {
  margin: 0 0 2px;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--sd-text);
}

.cc-campo small {
  font-weight: 400;
  color: var(--sd-muted);
}

.cc-campo input,
.cc-campo textarea {
  width: 100%;
  padding: 11px 14px;
  border: 1px solid var(--sd-border);
  border-radius: 12px;
  background: var(--sd-bg);
  font: inherit;
  color: var(--sd-text);
}

.cc-campo input:focus,
.cc-campo textarea:focus {
  border-color: var(--sd-primary);
  outline: 3px solid var(--sd-primary-soft);
}

.cc-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cc-chip {
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border: 1px solid var(--sd-border);
  border-radius: 999px;
  background: var(--sd-bg);
  font-size: 0.88rem;
  color: var(--sd-text);
  cursor: pointer;
  user-select: none;
}

.cc-chip input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.cc-chip.is-on {
  border-color: var(--sd-primary);
  background: var(--sd-primary-soft);
  font-weight: 600;
  color: var(--sd-primary);
}

.cc-chip:focus-within {
  outline: 3px solid var(--sd-primary-soft);
}

.cc-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.88rem;
  cursor: pointer;
}

.cc-check input {
  margin-top: 3px;
}

.cc-check-texto {
  color: var(--sd-muted);
}

.cc-error {
  margin: 0;
  font-size: 0.9rem;
  color: var(--sd-danger);
}

.cc-enviar {
  justify-self: start;
  height: 46px;
  padding: 0 24px;
}

.cc-form-ok {
  display: grid;
  justify-items: center;
  gap: 10px;
  padding: 24px 8px;
  text-align: center;
  color: var(--sd-text);
}

.cc-form-ok .material-symbols-outlined {
  font-size: 56px;
  color: var(--sd-success);
}

.cc-form-ok p {
  margin: 0;
  color: var(--sd-muted);
}

.cc-pie {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid var(--sd-border);
}

@media (max-width: 860px) {
  .cc-hero,
  .cc-voluntariado {
    grid-template-columns: minmax(0, 1fr);
  }

  .cc-hero-media {
    display: none;
  }
}
</style>
